import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAuth } from '../../contexts/AuthContext';
import { tipsterApi } from '../../services/tipsterService';
import { TipsterPublic, TipsterTierPublic } from '../../types/interfaces';
import { ROUTES } from '../../constants';
import ExpertPaidTierCard from '../../components/expertComponents/ExpertPaidTierCard';
import '../../styles/expert-buy-access.scss';

const ExpertBuyAccess: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [tipster, setTipster] = useState<TipsterPublic | null>(null);
  const [tiers, setTiers] = useState<TipsterTierPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTierId, setLoadingTierId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const tipsterId = parseInt(id);
    Promise.all([
      tipsterApi.getById(tipsterId),
      tipsterApi.getPaidTiers(tipsterId).catch(() => [] as TipsterTierPublic[]),
    ])
      .then(([tipsterData, tiersData]) => {
        setTipster(tipsterData);
        setTiers(tiersData.filter(t => t.level > 0));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const handleChoose = async (tierId: number) => {
    if (!id) return;

    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?redirect=${encodeURIComponent(`/experts/${id}/buy`)}`);
      return;
    }

    setError(null);
    setLoadingTierId(tierId);
    try {
      const { checkout_url } = await tipsterApi.createCheckout(parseInt(id), tierId);
      window.location.href = checkout_url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout. Please try again.');
      setLoadingTierId(null);
    }
  };

  if (loading) return <div className="expert-buy-access">{t.common.loading}</div>;

  return (
    <div className="expert-buy-access">
      <button
        className="expert-buy-access__back"
        onClick={() => navigate(`/experts/${id}`)}
        aria-label={t.common.back}
      >
        <FiArrowLeft size={14} /> {t.common.back}
      </button>

      <div className="buy-access__header">
        <h1 className="buy-access__title">
          {tipster?.full_name ? `${tipster.full_name}'s Plans` : 'Choose a Plan'}
        </h1>
        <p className="buy-access__subtitle">
          Buy 30-day access to this expert's premium picks and analysis.
        </p>
      </div>

      {error && <p className="buy-access__error">{error}</p>}

      {tiers.length === 0 ? (
        <p className="buy-access__empty">{t.manageMonetization.noPlans}</p>
      ) : (
        <div className="buy-access__grid">
          {tiers.map(tier => (
            <ExpertPaidTierCard
              key={tier.id}
              name={tier.name}
              priceMonthly={tier.price_monthly != null ? Number(tier.price_monthly) : null}
              featuresDescription={tier.features_description}
              featuresList={tier.features_list}
              featured={tier.featured}
              color={tier.color}
              activePicksCount={tier.active_picks_count}
              onClick={() => handleChoose(tier.id)}
              loading={loadingTierId === tier.id}
              showFeatureList
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ExpertBuyAccess;
